import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { usePicklistOptions } from '@/hooks/use-picklists'

interface DisqualifyLeadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string, details?: string) => void
  isPending?: boolean
}

export function DisqualifyLeadModal({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: DisqualifyLeadModalProps) {
  const disqualificationReasons = usePicklistOptions('lead', 'disqualification_reason')

  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!reason) {
      setError('Please select a reason for disqualification')
      return
    }

    onConfirm(reason, details.trim() || undefined)
    setReason('')
    setDetails('')
    setError('')
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason('')
      setDetails('')
      setError('')
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="disqualify-lead-description">
        <DialogHeader>
          <DialogTitle>Disqualify Lead</DialogTitle>
          <DialogDescription id="disqualify-lead-description">
            Please select a reason for disqualifying this lead.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="disqualify-reason" className="required">
              Reason *
            </Label>
            <select
              id="disqualify-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (error) setError('')
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-required="true"
              aria-invalid={!!error}
              aria-describedby={error ? 'disqualify-reason-error' : undefined}
            >
              <option value="">Select a reason...</option>
              {disqualificationReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {error && (
              <p
                id="disqualify-reason-error"
                className="text-sm text-destructive mt-1"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="disqualify-details">Details</Label>
            <Textarea
              id="disqualify-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Additional details (optional)"
              rows={3}
              aria-label="Disqualification details"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
              aria-label="Cancel disqualification"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isPending}
              aria-label={isPending ? 'Disqualifying lead...' : 'Confirm disqualification'}
            >
              {isPending ? 'Disqualifying...' : 'Disqualify'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
